import { Component, OnInit, HostListener, ElementRef, ChangeDetectorRef, ViewChild, NgZone, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Modal } from '../../components/ui/modal/modal';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { PostService } from '../../shared/services/post.service';
// import { ReactionSoundService } from '../../shared/services/reaction-sound.service';
import { CommentService, IComment, IReply } from '../../shared/services/comment.service';
import { UserService } from '../../shared/services/user.service';
import { DropdownItemComponent } from '../../components/ui/dropdown/dropdown-item/dropdown-item';
import { DropdownComponent } from '../../components/ui/dropdown/dropdown';
import { MentionInputDirective, MentionUser } from '../../shared/directives/mention-input.directive';
import { SwipeDirective } from '../../shared/directives/swipe.directive';
import { MentionDropdownComponent } from '../../components/ui/dropdown/mention-dropdown/mention-dropdown.component';
import { MentionHighlightPipe } from '../../shared/pipes/mention-highlight.pipe';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { FriendRequestService, FriendshipStatus } from '../../shared/services/friend-request.service';
import { RelationshipService } from '../../shared/services/relationship.service';
import { ConfirmationPopup } from '../../components/ui/confirmation-popup/confirmation-popup';
import { AuthStateService } from '../../shared/states/auth.state';
import { FavouriteService } from '../../shared/services/favourites.service';
import { ShareSheetComponent } from '../../components/ui/share-sheet/share-sheet';
import { ShareConfig } from '../../shared/services/share.service';
import { EditPostService, IEditPostRequest, IEditPostUser } from '../../shared/services/edit-post.service';
import { CollabrationRequestService } from '../../shared/services/collabration-request.service';
import { FriendRequestLimitPopup } from '../../components/ui/friend-request-limit-popup/friend-request-limit-popup';
import { SeoService } from '../../shared/services/seo.service';
import { AdminPreviewService } from '../../shared/services/admin-preview.service';
import { LinkPreviewComponent } from '../../components/ui/link-preview/link-preview';
import { ReportModal } from '../../components/ui/report-modal/report-modal';
import { BlockUserConfirm } from '../../components/ui/block-user-confirm/block-user-confirm';
import { ReportService } from '../../shared/services/report.service';

interface MediaItem {
  type: 'image' | 'video';
  url: string;
}

export type ReactionType = 'LIKE' | 'HEART' | 'HUG' | 'WOW' | 'SAD' | 'HAHA';

const REACTIONS: {
  type: ReactionType;
  emoji: string;
  label: string;
  color: string;
  bg: string;
}[] = [
    { type: 'LIKE', emoji: '👍', label: 'Like', color: '#2078f4', bg: '#e7f3ff' },
    { type: 'HEART', emoji: '❤️', label: 'Love', color: '#f33e58', bg: '#ffe7eb' },
    { type: 'HUG', emoji: '🤗', label: 'Hug', color: '#f7b125', bg: '#fff4e0' },
    { type: 'WOW', emoji: '😮', label: 'Wow', color: '#f7b125', bg: '#fff4e0' },
    { type: 'SAD', emoji: '😢', label: 'Sad', color: '#7c93a3', bg: '#eef2f5' },
    { type: 'HAHA', emoji: '😂', label: 'Haha', color: '#f7b125', bg: '#fff4e0' },
  ];

const EMOJI_CATEGORIES: { label: string; emojis: string[] }[] = [
  {
    label: 'Smileys',
    emojis: ['😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😌',
      '😍', '🥰', '😘', '😗', '😙', '😚', '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭'],
  },
  {
    label: 'Gestures',
    emojis: ['👍', '👎', '👊', '✊', '🤛', '🤜', '🤞', '✌️', '🤟', '🤘', '👌', '🤌', '🤏', '👈',
      '👉', '👆', '👇', '☝️', '👋', '🤚', '🖐', '✋', '🖖', '💪', '🦾', '🤲', '👐', '🙌', '👏'],
  },
  {
    label: 'Hearts',
    emojis: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓',
      '💗', '💖', '💘', '💝', '💟'],
  },
];

const QUICK_EMOJIS = ['😀', '❤️', '😄', '😂', '😍', '😘', '😈', '😡', '🔥', '👍', '🥺', '💔', '👏'];

interface ReactionCounts {
  LIKE: number;
  HEART: number;
  HUG: number;
  WOW: number;
  SAD: number;
  HAHA: number;
}

@Component({
  selector: 'app-post-details',
  standalone: true,
  imports: [
    CommonModule,
    ShareSheetComponent,
    FormsModule,
    Modal,
    DropdownComponent,
    DropdownItemComponent,
    MentionInputDirective,
    MentionDropdownComponent,
    MentionHighlightPipe,
    RouterModule,
    ConfirmationPopup,
    FriendRequestLimitPopup,
    LinkPreviewComponent,
    SwipeDirective,
    ReportModal,
    BlockUserConfirm
  ],
  templateUrl: './post-details.html'
})
export class PostDetails implements OnInit {
  readonly QUICK_EMOJIS = QUICK_EMOJIS;
  readonly EMOJI_CATEGORIES = EMOJI_CATEGORIES;
  readonly REACTIONS = REACTIONS;

  isModalOpen = true;
  showComments = false;
  showReactions = false;
  showCollaborators = false;
  showTaggedPeople = false;
  isOpen = false;
  private openCommentsOnLoad = false;
  private openEditOnLoad = false;

  private authState = inject(AuthStateService);
  private favouriteService = inject(FavouriteService);

  isFavourite = false;

  // ── Share sheet ──────────────────────────────────────────────────────────
  shareConfig: ShareConfig | null = null;

  // ── Friend request cooldown popup state ────────────────────────────────────
  showFriendLimitPopup = false;
  friendLimitHours: number | null = null;

  // ── Login prompt state ─────────────────────────────────────────────────────
  showLoginPrompt = false;
  loginPromptUserName = '';
  loginPromptAction = '';

  // Post data
  post: any = {
    user: {
      avatar: '',
      name: '',
      location: ''
    },
    media: [],
    description: '',
    isExpanded: false,
    timeAgo: '',
    activeSlide: 0,
    hashtags: [],
    hasBackground: false,
    backgroundPattern: '',
    backgroundBg: '',
    acceptedCollaborators: [],
    friendship: {
      status: 'NONE'
    } as { status: FriendshipStatus },
    friendshipStatus: 'NONE' as FriendshipStatus,
    friendshipRequestId: null as string | null,
    hostId: '',
    isMyPost: false,
    isFollowed: false
  };

  totalReactions = 0;
  commentCount = 0;
  shareCount = 0;
  myReaction: ReactionType | null = null;
  reactionCounts: ReactionCounts = {
    LIKE: 0,
    HEART: 0,
    HUG: 0,
    WOW: 0,
    SAD: 0,
    HAHA: 0
  };
  topTypes: ReactionType[] = [];
  recentUserName: string | null = null;

  isLoading = true;
  hasError = false;
  postId: string = '';
  commentsDisabled = false;
  togglingComments = false;

  // Reaction picker state
  showEmojiPicker = false;
  hoveredReaction: ReactionType | null = null;

  // Reactions modal state
  reactionData: any = null;
  reactionsLoading = false;
  activeReactionFilter: ReactionType | null = null;

  // Comments state
  comments: IComment[] = [];
  commentsTotal = 0;
  commentsPage = 1;
  commentsTotalPages = 1;
  commentsLoading = false;
  commentsLoaded = false;

  commentInput = '';
  replyInputs: Record<string, string> = {};
  replyingTo: string | null = null;

  editingCommentId: string | null = null;
  editInput = '';

  submitting = false;
  openDropdownId: string | null = null;

  // Emoji & GIF
  showCommentEmojiPicker = false;
  activeEmojiTab = 0;
  activeEmojiContext: string | null = null;
  showGifPicker = false;
  gifSearchQuery = '';
  gifResults: { id: string; url: string; preview: string }[] = [];
  gifLoading = false;
  selectedGifUrl: string | null = null;
  replyGifUrls: Record<string, string> = {};

  // @mention state
  activeMentionContext: string | null = null;
  mentionUsers: MentionUser[] = [];
  showMentionDropdown = false;

  @ViewChild('mainDir') mainDirRef?: MentionInputDirective;
  @ViewChild('replyDir') replyDirRef?: MentionInputDirective;

  private mention$ = new Subject<string>();
  private mentionSub = this.mention$
    .pipe(debounceTime(200), distinctUntilChanged())
    .subscribe(q => this.fetchMentionUsers(q));

  private GIPHY_KEY = 'mpVQUxeAA36qhojmJ6nnsrC6lekAAFfR';

  private friendRequestService = inject(FriendRequestService);
  private relationshipService = inject(RelationshipService);
  private seoService = inject(SeoService);
  private adminPreviewService = inject(AdminPreviewService);

  /** Opened from the admin panel via a preview link (not by the author) —
   *  the removed-by-admin banner then uses admin wording. */
  get isAdminPreview(): boolean {
    return !!this.adminPreviewService.findGrant('POST', this.postId);
  }



  constructor(
    private elRef: ElementRef,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private postService: PostService,
    // private soundService: ReactionSoundService,
    private commentService: CommentService,
    private userService: UserService,
    private ngZone: NgZone
  ) { }

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (!id) return;

      this.openCommentsOnLoad = this.route.snapshot.queryParamMap.get('openComments') === '1';
      this.openEditOnLoad = this.route.snapshot.queryParamMap.get('openEdit') === '1';
      this.postId = id;
      this.showComments = false;
      this.showReactions = false;
      this.showCollaborators = false;
      this.showTaggedPeople = false;
      this.comments = [];
      this.commentsTotal = 0;
      this.commentsPage = 1;
      this.commentsTotalPages = 1;
      this.commentsLoading = false;
      this.commentsLoaded = false;
      this.commentInput = '';
      this.replyInputs = {};
      this.replyGifUrls = {};
      this.replyingTo = null;
      this.selectedGifUrl = null;
      this.showGifPicker = false;
      this.showCommentEmojiPicker = false;
      this.closeMentionDropdown();
      this.getPostDetails(id);
    });

    // Reload after the header's edit modal saves this post, so the page
    // shows the updated caption/media instead of the stale copy.
    this.postService.onRefreshPosts$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.awaitingEditRefresh || this.isDeleting || this.isLeaving) return;
        this.awaitingEditRefresh = false;
        this.getPostDetails(this.postId);
      });
  }

  private getPostDetails(id: any): void {
    this.isLoading = true;
    this.hasError = false;

    this.postService.getPost(id).subscribe({
      next: (res: any) => {
        console.log('API Response:', res);
        const apiPost = res.data;

        const acceptedCollaborators = (apiPost.postInfo?.collaborations || [])
          .filter((collab: any) => collab.status === 'ACCEPTED')
          .map((collab: any) => {
            const user = collab.user;
            return {
              _id: user._id || user,
              displayName: user.displayName || '',
              userName: user.userName || '',
              profileImage: user.profileImage || undefined,
            };
          });
        this.post = {
          user: {
            avatar: apiPost.user.profileImage?.urlMedium ||
              apiPost.user.profileImage?.urlSmall ||
              this.avatarFallback(apiPost.user.displayName),
            name: apiPost.user.userName,
            location: apiPost.postInfo?.location
          },
          // Keep _id / original URLs — editPost() needs the real media ids,
          // otherwise the update sends an empty keep-list and the backend
          // deletes every media item on the post.
          media: apiPost.media.map((item: any) => ({
            _id: item._id ?? '',
            type: item.type,
            url: item.urlLarge || item.urlMedium || item.urlSmall || item.url,
            urlLarge: item.urlLarge,
            urlMedium: item.urlMedium,
          })),
          description: apiPost.title || '',
          isExpanded: false,
          timeAgo: this.getTimeAgo(apiPost.createdAt),
          activeSlide: 0,
          hashtags: apiPost.hashtags || [],
          hasBackground: !!(apiPost.postInfo?.backgroundPattern && apiPost.postInfo?.backgroundBg),
          backgroundPattern: apiPost.postInfo?.backgroundPattern || '',
          backgroundBg: apiPost.postInfo?.backgroundBg || '',
          acceptedCollaborators: acceptedCollaborators,
          rawCollaborations: apiPost.postInfo?.collaborations || [],
          rawMentions: apiPost.postInfo?.mentions || [],
          audience: apiPost.postInfo?.audience || 'EVERYONE',
          friendship: apiPost.friendship || { status: 'NONE' },
          friendshipStatus: (apiPost.friendship?.status ?? 'NONE') as FriendshipStatus,
          friendshipRequestId: apiPost.friendship?.requestId ?? null,
          hostId: apiPost.user?._id ?? '',
          isMyPost: apiPost.isMyPost || false,
          isFollowed: apiPost.isFollowed || false,
          isRemovedByAdmin: apiPost.isRemovedByAdmin || false
        };
        this.isFavourite = (apiPost as any).isFavourite ?? false;

        this.seoService.setSocialTags({
          title: this.post.description || `${this.post.user.name}'s post`,
          description: this.post.description || `Post by ${this.post.user.name} on Safeword`,
          image: this.post.media[0]?.url,
          path: `/post/${id}`,
        });

        this.commentsDisabled = apiPost.postInfo?.commentsDisabled ?? false;

        this.totalReactions = apiPost.reactions?.total || 0;
        this.commentCount = apiPost.numberOfComments || 0;
        this.shareCount = apiPost.numberOfShares || 0;
        this.myReaction = apiPost.reactions?.myReaction || null;
        this.reactionCounts = apiPost.reactions?.counts || {
          LIKE: 0,
          HEART: 0,
          HUG: 0,
          WOW: 0,
          SAD: 0,
          HAHA: 0
        };
        this.topTypes = (apiPost.reactions?.topTypes ?? []) as ReactionType[];
        this.recentUserName = apiPost.reactions?.recentUserName ?? null;

        console.log('Mapped post data:', this.post);

        if (this.openCommentsOnLoad) {
          this.openComments();
          this.openCommentsOnLoad = false;
        }

        if (this.openEditOnLoad && this.post.isMyPost) {
          this.openEditOnLoad = false;
          setTimeout(() => this.editPost(), 300);
        }

        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error fetching post:', err);
        this.isLoading = false;
        this.hasError = true;
        this.cdr.markForCheck();
      }
    });
  }

  // ── @mention methods ──────────────────────────────────────────────────────

  onMentionSearch(query: string, context: string): void {
    this.activeMentionContext = context;
    this.mention$.next(query);
  }

  onMentionDropdownChange(open: boolean, context: string): void {
    if (open) {
      this.activeMentionContext = context;
      this.showMentionDropdown = true;
      if (this.mentionUsers.length === 0) this.fetchMentionUsers('');
    } else if (this.activeMentionContext === context) {
      this.closeMentionDropdown();
    }
    this.cdr.markForCheck();
  }

  private fetchMentionUsers(q: string): void {
    this.userService.searchUsersForMention(q).subscribe({
      next: (res: any) => {
        this.mentionUsers = res?.data ?? [];
        this.showMentionDropdown = this.mentionUsers.length > 0;
        this.cdr.markForCheck();
      },
      error: () => { this.cdr.markForCheck(); },
    });
  }

  onMentionPicked(user: MentionUser, context: string, directive: MentionInputDirective): void {
    if (context === 'main') {
      this.commentInput = directive.insertMention(user, this.commentInput);
    } else {
      this.replyInputs[context] = directive.insertMention(user, this.replyInputs[context] ?? '');
    }
    this.closeMentionDropdown();
    this.cdr.markForCheck();
  }

  private closeMentionDropdown(): void {
    this.showMentionDropdown = false;
    this.activeMentionContext = null;
    this.mentionUsers = [];
  }

  /** Clicking a #hashtag chip takes the user to the "All" page, filtered to
   *  that exact hashtag across posts, blogs and events. */
  goToHashtag(tag: string, event?: Event): void {
    event?.stopPropagation();
    this.router.navigate(['/all'], { queryParams: { hashtag: tag } });
  }

  // ── Comments functionality ────────────────────────────────────────────────

  openComments(): void {
    this.showComments = true;
    this.showReactions = false;
    this.showCollaborators = false;
    this.showTaggedPeople = false;
    if (!this.commentsLoaded && !this.commentsLoading) {
      this.loadComments();
    }
  }

  closeComments(): void {
    this.showComments = false;
  }

  loadComments(page = 1): void {
    if (!this.postId) return;
    this.commentsLoading = true;
    this.cdr.markForCheck();

    this.commentService.getComments(this.postId, page, "POST").subscribe({
      next: (res: any) => {
        this.ngZone.run(() => {
          if (page === 1) {
            this.comments = res.data.items ?? [];
          } else {
            this.comments = [...this.comments, ...(res.data.items ?? [])];
          }
          this.commentsTotal = res.data.meta.total ?? 0;
          this.commentsPage = res.data.meta.page ?? 1;
          this.commentsTotalPages = res.data.meta.totalPages ?? 1;
          this.commentsLoading = false;
          this.commentsLoaded = true;
          this.cdr.markForCheck();
        });
      },
      error: () => {
        this.ngZone.run(() => {
          this.commentsLoading = false;
          this.commentsLoaded = true;
          this.cdr.markForCheck();
        });
      }
    });
  }

  toggleActionDropdown() {
    this.isOpen = !this.isOpen;
  }

  closeActionDropdown() {
    this.isOpen = false;
  }

  loadMoreComments(): void {
    if (this.commentsPage < this.commentsTotalPages) {
      this.loadComments(this.commentsPage + 1);
    }
  }

  submitComment(): void {
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(this.getPostDisplayName(), 'comment on this post by');
      return;
    }
    const body = this.commentInput.trim();
    if ((!body && !this.selectedGifUrl) || !this.postId || this.submitting) return;

    const finalBody = this.selectedGifUrl
      ? (body ? `${body}\n${this.selectedGifUrl}` : this.selectedGifUrl)
      : body;

    this.submitting = true;
    this.cdr.markForCheck();

    this.commentService.createComment({
      targetType: 'POST',
      targetId: this.postId,
      body: finalBody,
    }).subscribe({
      next: (res: any) => {
        const newComment: IComment = {
          ...res.data,
          replies: [],
          ableToReply: true,
          ableToModification: true,
        };
        this.comments = [newComment, ...this.comments];
        this.commentsTotal++;
        this.commentCount++;
        this.commentInput = '';
        if (this.mainDirRef) this.mainDirRef.setText('');
        this.selectedGifUrl = null;
        this.showCommentEmojiPicker = false;
        this.showGifPicker = false;
        this.submitting = false;
        this.closeMentionDropdown();
        this.cdr.markForCheck();
      },
      error: () => { this.submitting = false; this.cdr.markForCheck(); }
    });
  }

  submitReply(parentId: string): void {
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(this.getPostDisplayName(), 'comment on this post by');
      return;
    }
    const body = (this.replyInputs[parentId] ?? '').trim();
    const gifUrl = this.replyGifUrls[parentId] ?? null;
    if ((!body && !gifUrl) || !this.postId || this.submitting) return;

    const finalBody = gifUrl
      ? (body ? `${body}\n${gifUrl}` : gifUrl)
      : body;

    this.submitting = true;
    this.cdr.markForCheck();

    this.commentService.createComment({
      targetType: 'POST',
      targetId: this.postId,
      parentId,
      body: finalBody,
    }).subscribe({
      next: (res: any) => {
        const newReply: IReply = { ...res.data, ableToModification: true };
        const parent = this.comments.find(c => c._id === parentId);
        if (parent) {
          parent.replies = [newReply, ...(parent.replies ?? [])];
          parent.replyCount = (parent.replyCount ?? 0) + 1;
        }
        this.commentCount++;
        this.replyInputs[parentId] = '';
        delete this.replyGifUrls[parentId];
        this.replyingTo = null;
        if (this.replyDirRef) this.replyDirRef.setText('');
        this.showGifPicker = false;
        this.submitting = false;
        this.closeMentionDropdown();
        this.cdr.markForCheck();
      },
      error: () => { this.submitting = false; this.cdr.markForCheck(); }
    });
  }

  toggleReply(commentId: string): void {
    this.replyingTo = this.replyingTo === commentId ? null : commentId;
    if (this.replyingTo && !this.replyInputs[commentId]) {
      this.replyInputs[commentId] = '';
    }
    this.cdr.markForCheck();
    if (this.replyingTo) {
      setTimeout(() => {
        document.getElementById('reply-input-' + commentId)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }

  toggleDropdown(commentId: string): void {
    this.openDropdownId = this.openDropdownId === commentId ? null : commentId;
    this.cdr.markForCheck();
  }

  closeDropdown(): void {
    this.openDropdownId = null;
    this.cdr.markForCheck();
  }

  startEdit(item: IComment | IReply): void {
    this.editingCommentId = item._id;
    this.editInput = item.body;
    this.cdr.markForCheck();
  }

  cancelEdit(): void {
    this.editingCommentId = null;
    this.editInput = '';
    this.cdr.markForCheck();
  }

  saveEdit(item: IComment | IReply): void {
    const body = this.editInput.trim();
    if (!body || this.submitting) return;
    this.submitting = true;
    this.cdr.markForCheck();

    this.commentService.updateComment(item._id, body).subscribe({
      next: (res: any) => {
        item.body = res.data.body;
        item.isEdited = true;
        this.editingCommentId = null;
        this.editInput = '';
        this.submitting = false;
        this.cdr.markForCheck();
      },
      error: () => { this.submitting = false; this.cdr.markForCheck(); }
    });
  }

  deleteComment(commentId: string, parentId?: string): void {
    this.commentService.deleteComment(commentId).subscribe({
      next: (res: any) => {
        if (parentId) {
          const parent = this.comments.find(c => c._id === parentId);
          if (parent) {
            parent.replies = parent.replies.filter(r => r._id !== commentId);
            parent.replyCount = Math.max(0, (parent.replyCount ?? 1) - 1);
          }
        } else {
          this.comments = this.comments.filter(c => c._id !== commentId);
          this.commentsTotal = Math.max(0, this.commentsTotal - 1);
        }
        // The API returns the post's real count (deleting a comment also
        // removes its replies), so use that rather than guessing locally.
        this.commentCount = res?.data?.numberOfComments ?? Math.max(0, this.commentCount - 1);
        this.cdr.markForCheck();
      }
    });
  }

  // ── Report comment modal ────────────────────────────────────────────────
  isCommentReportModalOpen = false;
  isSubmittingCommentReport = false;
  commentReportError = '';
  commentReportSuccessOpen = false;
  private reportingCommentId: string | null = null;

  canReportComment(comment: IComment | IReply): boolean {
    return this.isLoggedIn() && !comment.ableToModification;
  }

  reportComment(commentId: string): void {
    this.closeDropdown();
    this.reportingCommentId = commentId;
    this.commentReportError = '';
    this.isCommentReportModalOpen = true;
    this.cdr.markForCheck();
  }

  closeCommentReportModal(): void {
    if (this.isSubmittingCommentReport) return;
    this.isCommentReportModalOpen = false;
    this.commentReportError = '';
    this.cdr.markForCheck();
  }

  submitCommentReport(description: string): void {
    if (!this.reportingCommentId) return;
    this.isSubmittingCommentReport = true;
    this.commentReportError = '';

    this.reportService.submitCommentReport(this.reportingCommentId, description).subscribe({
      next: () => {
        this.isSubmittingCommentReport = false;
        this.isCommentReportModalOpen = false;
        this.commentReportSuccessOpen = true;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.isSubmittingCommentReport = false;
        this.commentReportError = err?.error?.message ?? 'Failed to submit report. Please try again.';
        this.cdr.markForCheck();
      },
    });
  }

  closeCommentReportSuccess(): void {
    this.commentReportSuccessOpen = false;
    this.cdr.markForCheck();
  }

  appendQuickEmoji(emoji: string): void {
    if (this.editingCommentId !== null) {
      this.editInput += emoji;
    } else if (this.replyingTo) {
      const id = this.replyingTo;
      this.replyInputs[id] = (this.replyInputs[id] ?? '') + emoji;
      if (this.replyDirRef) this.replyDirRef.setText(this.replyInputs[id]);
    } else {
      this.commentInput += emoji;
      if (this.mainDirRef) this.mainDirRef.setText(this.commentInput);
    }
    this.cdr.markForCheck();
  }

  toggleEmojiPicker(e: MouseEvent, context: 'main' | string = 'main'): void {
    e.stopPropagation();
    this.activeEmojiContext = context;
    this.showCommentEmojiPicker = !this.showCommentEmojiPicker;
    this.showGifPicker = false;
    this.cdr.markForCheck();
  }

  pickEmoji(emoji: string): void {
    if (this.editingCommentId !== null) {
      this.editInput += emoji;
    } else if (this.activeEmojiContext && this.activeEmojiContext !== 'main') {
      const id = this.activeEmojiContext;
      this.replyInputs[id] = (this.replyInputs[id] ?? '') + emoji;
      if (this.replyDirRef) this.replyDirRef.setText(this.replyInputs[id]);
    } else {
      this.commentInput += emoji;
      if (this.mainDirRef) this.mainDirRef.setText(this.commentInput);
    }
    this.showCommentEmojiPicker = false;
    this.cdr.markForCheck();
  }

  setEmojiTab(index: number): void {
    this.activeEmojiTab = index;
    this.cdr.markForCheck();
  }

  toggleGifPicker(context: 'main' | string = 'main', e: MouseEvent): void {
    e.stopPropagation();
    this.activeEmojiContext = context;
    this.showGifPicker = !this.showGifPicker;
    this.showCommentEmojiPicker = false;
    if (this.showGifPicker && this.gifResults.length === 0) {
      this.searchGifs('trending');
    }
    this.cdr.markForCheck();
  }

  searchGifs(query?: string): void {
    const q = query ?? this.gifSearchQuery.trim();
    if (!q) return;
    this.gifLoading = true;
    this.cdr.markForCheck();

    const url = q === 'trending'
      ? `https://api.giphy.com/v1/gifs/trending?api_key=${this.GIPHY_KEY}&limit=20&rating=g`
      : `https://api.giphy.com/v1/gifs/search?api_key=${this.GIPHY_KEY}&q=${encodeURIComponent(q)}&limit=20&rating=g`;

    fetch(url)
      .then(r => r.json())
      .then((data: any) => {
        this.gifResults = (data.data ?? []).map((g: any) => ({
          id: g.id,
          url: g.images.original.url,
          preview: g.images.fixed_height_small.url,
        }));
        this.gifLoading = false;
        this.cdr.markForCheck();
      })
      .catch(() => { this.gifLoading = false; this.cdr.markForCheck(); });
  }

  selectGif(gif: { url: string; preview: string }): void {
    if (this.activeEmojiContext && this.activeEmojiContext !== 'main') {
      this.replyGifUrls[this.activeEmojiContext] = gif.url;
    } else {
      this.selectedGifUrl = gif.url;
    }
    this.showGifPicker = false;
    this.cdr.markForCheck();
  }

  removeGif(context: 'main' | string = 'main'): void {
    if (context !== 'main') {
      delete this.replyGifUrls[context];
    } else {
      this.selectedGifUrl = null;
    }
    this.cdr.markForCheck();
  }

  onGifSearchKey(e: KeyboardEvent): void {
    if (e.key === 'Enter') this.searchGifs();
  }

  isGifBody(body: string): boolean {
    return /^https?:\/\/.*\.(gif|giphy\.com)/.test(body.trim());
  }

  getTextPart(body: string): string {
    return body.split('\n').filter(l => !this.isGifBody(l)).join('\n').trim();
  }

  getGifPart(body: string): string | null {
    return body.split('\n').find(l => this.isGifBody(l)) ?? null;
  }

  /** Single source of truth for the ui-avatars fallback used across this page. */
  avatarFallback(name?: string | null): string {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'U')}&background=FFD4E0&color=D31148`;
  }

  getUserAvatar(item: IComment | IReply): string {
    return item.user?.profileImage?.urlSmall
      ?? this.avatarFallback(item.user?.displayName);
  }

  getCurrentUserAvatar(): string {
    const user = this.authState.getAuthSnapshot()?.user;

    return user?.profileImage?.urlSmall
      ?? user?.profileImage?.urlMedium
      ?? this.avatarFallback(user?.displayName || user?.userName);
  }

  // ── Reactions functionality ───────────────────────────────────────────────

  openReactions(): void {
    this.showReactions = true;
    this.showComments = false;
    this.showCollaborators = false;
    this.showTaggedPeople = false;
    this.activeReactionFilter = null;
    this.loadReactions(this.postId);
  }

  closeReactions(): void {
    this.showReactions = false;
    this.reactionData = null;
  }

  closeCommentsAndReactions(): void {
    this.showComments = false;
    this.showReactions = false;
    this.showCollaborators = false;
    this.showTaggedPeople = false;
  }

  openCollaborators(): void {
    this.showCollaborators = true;
    this.showComments = false;
    this.showReactions = false;
    this.showTaggedPeople = false;
  }

  closeCollaborators(): void {
    this.showCollaborators = false;
  }

  openTaggedPeople(event?: Event): void {
    event?.stopPropagation();
    this.showTaggedPeople = true;
    this.showComments = false;
    this.showReactions = false;
    this.showCollaborators = false;
  }

  closeTaggedPeople(): void {
    this.showTaggedPeople = false;
  }

  closeModal(): void {
    this.isModalOpen = false;
    this.showComments = false;
    this.showReactions = false;
    this.showCollaborators = false;
    this.showTaggedPeople = false;
    this.navigateBack();
  }

  /** Navigate to returnUrl from router state, or fall back to /posts */
  private navigateBack(): void {
    const state = (window.history.state ?? {}) as { returnUrl?: string };
    if (state?.returnUrl) {
      // returnUrl is only set when we were opened from inside the app, so the
      // previous history entry is that page. Going back (instead of pushing a
      // new navigation) keeps the router from scrolling it to the top first,
      // which made the restored list flash.
      window.history.back();
    } else {
      this.router.navigate(['/posts']);
    }
  }

  toggleComments(): void {
    if (this.togglingComments) return;
    this.togglingComments = true;
    this.postService.toggleComments(this.postId).subscribe({
      next: (res: any) => {
        this.commentsDisabled = res.data?.postInfo?.commentsDisabled ?? !this.commentsDisabled;
        this.togglingComments = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.togglingComments = false;
        this.cdr.markForCheck();
      }
    });
  }

  toggleExpandPost(): void {
    this.post.isExpanded = !this.post.isExpanded;
  }

  nextSlide(event?: Event): void {
    if (event) event.stopPropagation();
    if (!this.post.media || this.post.media.length === 0) return;
    this.post.activeSlide = (this.post.activeSlide + 1) % this.post.media.length;
    this.cdr.markForCheck();
  }

  prevSlide(event?: Event): void {
    if (event) event.stopPropagation();
    if (!this.post.media || this.post.media.length === 0) return;
    this.post.activeSlide = this.post.activeSlide === 0
      ? this.post.media.length - 1
      : this.post.activeSlide - 1;
    this.cdr.markForCheck();
  }

  goToSlide(index: number, event?: Event): void {
    if (event) event.stopPropagation();
    this.post.activeSlide = index;
    this.cdr.markForCheck();
  }

  getTimeAgo(dateString: string): string {
    const now = new Date().getTime();
    const past = new Date(dateString).getTime();
    const diff = Math.floor((now - past) / 1000);

    if (diff < 60) return 'just now';
    const m = Math.floor(diff / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d ago`;
    const w = Math.floor(d / 7);
    if (w < 4) return `${w}w ago`;
    return new Date(dateString).toLocaleDateString();
  }

  /**
   * Returns the CSS pattern class for background posts
   */
  getPatternClass(patternId?: string): string {
    if (!patternId) return '';

    const patternMap: Record<string, string> = {
      'p-checks-purple': 'pattern-checks-md',
      'p-zigzag-sky': 'pattern-zigzag-md',
      'p-grid-green': 'pattern-grid-md',
      'p-diag-orange': 'pattern-diagonal-lines-md',
      'p-hlines-teal': 'pattern-horizontal-lines-md',
      'p-vstripe-rose': 'pattern-vertical-stripes-md',
      'p-crossdots-navy': 'pattern-cross-dots-lg',
      'p-dstripe-amber': 'pattern-diagonal-stripes-md',
      'p-h-indigo': 'pattern-h-lg',
    };

    return patternMap[patternId] || '';
  }

  private getPostDisplayName(): string {
    const collaboratorNames = (this.post.acceptedCollaborators ?? [])
      .map((c: any) => c.userName)
      .join(', ');
    return collaboratorNames
      ? `${this.post.user?.name} & ${collaboratorNames}`
      : (this.post.user?.name ?? '');
  }

  onReactionClick(): void {
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(this.getPostDisplayName(), 'react to this post by');
      return;
    }
    if (this.showEmojiPicker) {
      this.showEmojiPicker = false;
    } else {
      // this.soundService.playPickerOpen();
      this.showEmojiPicker = true;
    }
    this.cdr.detectChanges();
  }

  closeEmojiPickerBar(): void {
    this.showEmojiPicker = false;
    this.hoveredReaction = null;
    this.cdr.detectChanges();
  }

  onEmojiPick(type: ReactionType, $event: MouseEvent): void {
    $event.stopPropagation();
    this.showEmojiPicker = false;
    this.hoveredReaction = null;
    // this.soundService.playReactionSelect(type);
    this.submitReaction(type);
  }

  private submitReaction(type: ReactionType): void {
    const previous = this.myReaction;
    const previousCounts = { ...this.reactionCounts };
    const previousTotal = this.totalReactions;
    const previousTopTypes = [...this.topTypes];
    const previousRecentUserName = this.recentUserName;

    if (previous === type) {
      this.reactionCounts[type] = Math.max(0, this.reactionCounts[type] - 1);
      this.totalReactions = Math.max(0, this.totalReactions - 1);
      this.myReaction = null;
    } else {
      if (previous) {
        this.reactionCounts[previous] = Math.max(0, this.reactionCounts[previous] - 1);
        this.totalReactions = Math.max(0, this.totalReactions - 1);
      }
      this.reactionCounts[type]++;
      this.totalReactions++;
      this.myReaction = type;
    }

    // Update topTypes and recentUserName immediately
    this.topTypes = this.computeTopTypes(this.reactionCounts);
    const currentUserName = this.authState.getAuthSnapshot()?.user?.userName ?? null;
    if (this.myReaction) {
      this.recentUserName = currentUserName;
    } else if (this.totalReactions === 0) {
      this.recentUserName = null;
    }

    this.cdr.detectChanges();

    this.postService.reactToPost(this.postId, type).subscribe({
      error: () => {
        this.myReaction = previous;
        this.reactionCounts = previousCounts;
        this.totalReactions = previousTotal;
        this.topTypes = previousTopTypes;
        this.recentUserName = previousRecentUserName;
        this.cdr.detectChanges();
      }
    });
  }

  private computeTopTypes(counts: ReactionCounts): ReactionType[] {
    return (Object.entries(counts) as [ReactionType, number][])
      .filter(([, count]) => count > 0)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([type]) => type);
  }

  getReactionConfig(type: ReactionType | null): typeof REACTIONS[0] | null {
    if (!type) return null;
    return REACTIONS.find(r => r.type === type) ?? null;
  }

  setReactionFilter(type: ReactionType | null): void {
    this.activeReactionFilter = type;
    if (this.postId) {
      this.loadReactions(this.postId, type ?? undefined);
    }
  }

  private loadReactions(eventId: string, type?: ReactionType): void {
    this.reactionsLoading = true;
    this.cdr.detectChanges();

    this.postService.getReactions(eventId, type).subscribe({
      next: (res: any) => {
        this.reactionData = res?.data;
        this.reactionsLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.reactionsLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  get filteredReactions(): any[] {
    return this.reactionData?.all ?? [];
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(e: MouseEvent): void {
    const target = e.target as HTMLElement;
    const commentPickerArea = this.elRef.nativeElement.querySelector('.comment-picker-area');

    if (commentPickerArea && !commentPickerArea.contains(target)) {
      this.showCommentEmojiPicker = false;
      this.showGifPicker = false;
      this.closeMentionDropdown();
    }

    const reactionPicker = this.elRef.nativeElement.querySelector('.reaction-picker');
    // Leave the picker alone when the click was on its own trigger button —
    // onReactionClick() already toggled it. (This used to test for an <svg> in
    // the clicked button, but once you've reacted the button shows your emoji
    // instead of the thumbs-up svg, so the picker closed as soon as it opened.)
    if (reactionPicker && !reactionPicker.contains(target) && !target.closest('.reaction-trigger')) {
      this.showEmojiPicker = false;
      this.cdr.detectChanges();
    }
  }

  showCollabModal = false;
  selectedPost: any | null = null;

  // ── Edit / Delete / Leave collab state ────────────────────────────────────
  showActionMenu = false;
  showDeleteConfirm = false;
  showLeaveConfirm = false;
  isDeleting = false;

  // ── Report post modal ────────────────────────────────────────────────────
  isReportModalOpen = false;
  isSubmittingReport = false;
  reportError = '';
  reportSuccessOpen = false;
  private reportService = inject(ReportService);
  isLeaving = false;

  // ── Block user (safeword-confirm) modal ─────────────────────────────────
  showBlockUserConfirm = false;

  /** Bound to <app-block-user-confirm>'s [targetName] — "You're about to
   *  block {name}". */
  get postAuthorDisplayName(): string {
    return this.post?.user?.name ?? this.post?.hostName ?? '';
  }

  get isCollaborator(): boolean {
    const authUser = this.authState.getAuthSnapshot()?.user;
    const userId = authUser?._id ?? authUser?.id;
    if (!userId || !this.post) return false;
    return (this.post.rawCollaborations ?? []).some(
      (c: any) => {
        const collabUserId = c.user?._id ?? c.user?.id ?? c.user;
        return collabUserId === userId || collabUserId?.toString() === userId?.toString();
      },
    );
  }

  /** Returns the current user's collab status: 'PENDING' | 'ACCEPTED' | 'DECLINED' | null */
  get myCollabStatus(): 'PENDING' | 'ACCEPTED' | 'DECLINED' | null {
    const authUser = this.authState.getAuthSnapshot()?.user;
    const userId = authUser?._id ?? authUser?.id;
    if (!userId || !this.post) return null;
    const collab = (this.post.rawCollaborations ?? []).find((c: any) => {
      const collabUserId = c.user?._id ?? c.user?.id ?? c.user;
      return collabUserId === userId || collabUserId?.toString() === userId?.toString();
    });
    return collab?.status ?? null;
  }

  // ── Collaboration accept / decline ────────────────────────────────────────
  private collabService = inject(CollabrationRequestService);
  isCollabActioning = false;

  acceptCollaboration(): void {
    if (this.isCollabActioning) return;
    this.isCollabActioning = true;
    this.cdr.markForCheck();
    this.collabService.postCollabrationAccept(this.postId).subscribe({
      next: () => {
        this.updateMyCollabStatus('ACCEPTED');
        this.isCollabActioning = false;
        this.postService.triggerRefresh();
        this.cdr.markForCheck();
      },
      error: () => { this.isCollabActioning = false; this.cdr.markForCheck(); },
    });
  }

  declineCollaboration(): void {
    if (this.isCollabActioning) return;
    this.isCollabActioning = true;
    this.cdr.markForCheck();
    this.collabService.postCollabrationDecline(this.postId).subscribe({
      next: () => {
        this.updateMyCollabStatus('DECLINED');
        this.isCollabActioning = false;
        this.postService.triggerRefresh();
        this.cdr.markForCheck();
      },
      error: () => { this.isCollabActioning = false; this.cdr.markForCheck(); },
    });
  }

  private updateMyCollabStatus(status: 'ACCEPTED' | 'DECLINED'): void {
    const authUser = this.authState.getAuthSnapshot()?.user;
    const userId = authUser?._id ?? authUser?.id;
    const collab = (this.post?.rawCollaborations ?? []).find((c: any) => {
      const id = c.user?._id ?? c.user?.id ?? c.user;
      return id === userId || id?.toString() === userId?.toString();
    });
    if (collab) collab.status = status;
  }

  toggleActionMenu(e: MouseEvent): void {
    e.stopPropagation();
    this.showActionMenu = !this.showActionMenu;
    this.cdr.markForCheck();
  }

  closeActionMenu(): void {
    this.showActionMenu = false;
    this.cdr.markForCheck();
  }

  private editPostService = inject(EditPostService);
  private destroyRef = inject(DestroyRef);
  private awaitingEditRefresh = false;

  editPost(): void {
    this.closeActionMenu();

    // Build collaborators list with their current status
    const rawCollabs: any[] = this.post.rawCollaborations ?? [];
    const collaborators: IEditPostUser[] = rawCollabs
      .filter((c: any) => c.user && typeof c.user === 'object')
      .map((c: any) => ({
        _id: c.user._id ?? c.user.id ?? '',
        displayName: c.user.displayName ?? '',
        userName: c.user.userName ?? '',
        profileImage: c.user.profileImage ?? null,
        collabStatus: (c.status ?? 'PENDING') as 'ACCEPTED' | 'PENDING' | 'DECLINED',
      }));

    // Tagged people come from postInfo.mentions (populated as user objects)
    const rawMentions: any[] = (this.post as any).rawMentions ?? [];
    const taggedPeople: IEditPostUser[] = rawMentions
      .filter((u: any) => u && typeof u === 'object' && u._id)
      .map((u: any) => ({
        _id: u._id ?? '',
        displayName: u.displayName ?? '',
        userName: u.userName ?? '',
        profileImage: u.profileImage ?? null,
      }));

    const editRequest: IEditPostRequest = {
      postId: this.postId,
      caption: this.post.description ?? '',
      backgroundPattern: this.post.backgroundPattern || undefined,
      backgroundBg: this.post.backgroundBg || undefined,
      location: this.post.user?.location || undefined,
      audience: (this.post as any).audience || 'EVERYONE',
      hashtags: this.post.hashtags ?? [],
      existingMedia: (this.post.media ?? []).map((m: any) => ({
        _id: m._id ?? '',
        url: m.urlLarge || m.urlMedium || m.url || '',
        urlLarge: m.urlLarge,
        urlMedium: m.urlMedium,
        type: m.type as 'image' | 'video',
      })),
      taggedPeople,
      collaborators,
    };
    this.closeModal();
    this.awaitingEditRefresh = true;
    setTimeout(() => this.editPostService.openEditModal(editRequest), 200);
  }

  confirmDelete(): void {
    this.closeActionMenu();
    this.showDeleteConfirm = true;
    this.cdr.markForCheck();
  }

  cancelDelete(): void {
    this.showDeleteConfirm = false;
    this.cdr.markForCheck();
  }

  doDeletePost(): void {
    if (this.isDeleting) return;
    this.isDeleting = true;
    this.cdr.markForCheck();

    this.postService.deletePost(this.postId).subscribe({
      next: () => {
        this.isDeleting = false;
        this.showDeleteConfirm = false;
        this.postService.triggerRefresh();
        this.navigateBack();
      },
      error: (err) => {
        this.isDeleting = false;
        this.showDeleteConfirm = false;
        console.error('Delete post error:', err);
        this.cdr.markForCheck();
      },
    });
  }

  confirmLeave(): void {
    this.closeActionMenu();
    this.showLeaveConfirm = true;
    this.cdr.markForCheck();
  }

  // ── Block user (from the post's 3-dot menu) ───────────────────────────────

  blockPostUser(): void {
    this.closeActionMenu();
    this.showBlockUserConfirm = true;
    this.cdr.markForCheck();
  }

  cancelBlockPostUser(): void {
    this.showBlockUserConfirm = false;
    this.cdr.markForCheck();
  }

  confirmBlockPostUser(): void {
    this.showBlockUserConfirm = false;
    const hostId = this.post?.hostId;
    this.cdr.markForCheck();
    if (!hostId) return;

    this.friendRequestService.block(hostId).subscribe({
      next: () => {
        this.friendRequestService.triggerRefresh();
        // The author's content is no longer visible to us once blocked —
        // this detail page can no longer be shown, so leave it.
        this.navigateBack();
      },
      error: (err) => {
        console.error('Block user failed:', err);
        this.cdr.markForCheck();
      },
    });
  }

  reportPost(): void {
    this.closeActionMenu();
    this.reportError = '';
    this.isReportModalOpen = true;
    this.cdr.markForCheck();
  }  closeReportModal(): void {
    if (this.isSubmittingReport) return;
    this.isReportModalOpen = false;
    this.reportError = '';
    this.cdr.markForCheck();
  }

  submitPostReport(description: string): void {
    if (!this.postId) return;
    this.isSubmittingReport = true;
    this.reportError = '';

    this.reportService.submitReport(this.postId, description).subscribe({
      next: () => {
        this.isSubmittingReport = false;
        this.isReportModalOpen = false;
        this.reportSuccessOpen = true;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.isSubmittingReport = false;
        this.reportError = err?.error?.message ?? 'Failed to submit report. Please try again.';
        this.cdr.markForCheck();
      },
    });
  }

  closeReportSuccess(): void {
    this.reportSuccessOpen = false;
    this.cdr.markForCheck();
  }

  cancelLeave(): void {
    this.showLeaveConfirm = false;
    this.cdr.markForCheck();
  }

  doLeaveCollaboration(): void {
    if (this.isLeaving) return;
    this.isLeaving = true;
    this.cdr.markForCheck();

    this.postService.leaveCollaboration(this.postId).subscribe({
      next: () => {
        this.isLeaving = false;
        this.showLeaveConfirm = false;
        this.postService.triggerRefresh();
        this.navigateBack();
      },
      error: (err) => {
        this.isLeaving = false;
        this.showLeaveConfirm = false;
        console.error('Leave collaboration error:', err);
        this.cdr.markForCheck();
      },
    });
  }

  showCollaboratorsModal(post: any): void {
    this.selectedPost = post;
    this.showCollabModal = true;
  }

  onFriendButtonClick(card: any, event: MouseEvent): void {
    event.stopPropagation();
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(this.getPostDisplayName(), 'add as a friend');
      return;
    }
    switch (card.friendship.status) {
      case 'NONE': this.sendFriendRequest(card, event); break;
      case 'PENDING_SENT': this.cancelFriendRequest(card, event); break;
      case 'PENDING_RECEIVED': this.acceptFriendRequest(card, event); break;
      case 'FRIENDS': this.promptUnfriend(card, event); break;
      case 'BLOCKED': break;
    }
  }

  showUnfriendConfirm = false;
  private pendingUnfriendAction: (() => void) | null = null;

  private promptUnfriend(card: any, event: MouseEvent): void {
    event.stopPropagation();
    this.pendingUnfriendAction = () => this.unfriend(card, event);
    this.showUnfriendConfirm = true;
  }

  confirmUnfriend(): void {
    this.showUnfriendConfirm = false;
    this.pendingUnfriendAction?.();
    this.pendingUnfriendAction = null;
  }

  cancelUnfriendConfirm(): void {
    this.showUnfriendConfirm = false;
    this.pendingUnfriendAction = null;
  }

  /** They've sent you a request — clicking the friend icon accepts it. (It
   *  used to call sendFriendRequest, which bails unless the status is NONE,
   *  so the button did nothing — SAF-839.) */
  private acceptFriendRequest(card: any, event: MouseEvent): void {
    event.stopPropagation();
    if (card.friendship.status !== 'PENDING_RECEIVED' || !card.friendship.requestId) return;
    card.friendship.status = 'FRIENDS';
    this.cdr.markForCheck();
    this.friendRequestService.accept(card.friendship.requestId, card.hostId).subscribe({
      error: () => { card.friendship.status = 'PENDING_RECEIVED'; this.cdr.markForCheck(); },
    });
  }

  private sendFriendRequest(card: any, event: MouseEvent): void {
    event.stopPropagation();
    if (card.friendship.status !== 'NONE') return;
    card.friendship.status = 'PENDING_SENT';
    this.cdr.markForCheck();
    this.friendRequestService.send(card.hostId).subscribe({
      next: (res: any) => { card.friendship.requestId = res?.data?._id ?? null; this.cdr.markForCheck(); },
      error: (err) => {
        const status = err?.error?.statusCode ?? err?.status;
        if (status !== 409) card.friendship.status = 'NONE';
        const cooldownHours = this.friendRequestService.parseCooldownError(err);
        if (cooldownHours !== null) {
          this.friendLimitHours = cooldownHours;
          this.showFriendLimitPopup = true;
        }
        this.cdr.markForCheck();
      },
    });
  }

  private cancelFriendRequest(card: any, event: MouseEvent): void {
    event.stopPropagation();
    if (!card.friendship.requestId) return;
    const prevStatus = card.friendship.status;
    const prevRequestId = card.friendship.requestId;
    card.friendship.status = 'NONE';
    this.cdr.markForCheck();
    this.friendRequestService.cancel(card.friendship.requestId).subscribe({
      error: () => { card.friendship.status = prevStatus; card.friendship.requestId = prevRequestId; this.cdr.markForCheck(); },
    });
  }

  private unfriend(card: any, event: MouseEvent): void {
    event.stopPropagation();
    const prevStatus = card.friendship.status;
    const prevRequestId = card.friendship.requestId;
    card.friendship.status = 'NONE';
    card.friendship.requestId = null;
    this.cdr.markForCheck();
    this.friendRequestService.unfriend(card.hostId).subscribe({
      error: () => { card.friendship.status = prevStatus; card.friendship.requestId = prevRequestId; this.cdr.markForCheck(); },
    });
  }

  friendButtonTitle(status: FriendshipStatus): string {
    const map: Record<FriendshipStatus, string> = {
      NONE: 'Send friend request',
      PENDING_SENT: 'Cancel friend request',
      PENDING_RECEIVED: 'Accept friend request',
      FRIENDS: 'Unfriend',
      BLOCKED: 'Blocked',
    };
    return map[status];
  }

  follow(event: MouseEvent, card: any): void {
    event.stopPropagation();

    // ── Auth guard ──
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(card.user?.name ?? card.hostName, 'follow');
      return;
    }

    card.isFollowed = true;
    this.cdr.detectChanges();
    this.relationshipService.follow(card.hostId).subscribe({
      error: () => { card.isFollowed = false; this.cdr.detectChanges(); },
    });
  }

  unfollow(event: MouseEvent | undefined, card: any): void {
    event?.stopPropagation();

    // ── Auth guard ──
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(card.user?.name ?? card.hostName, 'unfollow');
      return;
    }

    card.isFollowed = false;
    this.cdr.detectChanges();
    this.relationshipService.unfollow(card.hostId).subscribe({
      error: () => { card.isFollowed = true; this.cdr.detectChanges(); },
    });
  }

  onModalFollowClick(item: any, event: MouseEvent): void {
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(item.user?.userName ?? item.user?.userName, 'follow');
      return;
    }
    event.stopPropagation();
    if (item.isFollowed) {
      item.isFollowed = false;
      this.cdr.markForCheck();
      this.relationshipService.unfollow(item.user?._id).subscribe({
        error: () => { item.isFollowed = true; this.cdr.markForCheck(); }
      });
    } else {
      item.isFollowed = true;
      this.cdr.markForCheck();
      this.relationshipService.follow(item.user?._id).subscribe({
        error: () => { item.isFollowed = false; this.cdr.markForCheck(); }
      });
    }
  }

  onModalFriendClick(item: any, event: MouseEvent): void {
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(item.user?.userName ?? item.user?.userName, 'add as a friend');
      return;
    }
    event.stopPropagation();
    const status = item.friendship.status ?? 'NONE';

    if (status === 'NONE') {
      item.friendship.status = 'PENDING_SENT';
      this.cdr.markForCheck();
      this.friendRequestService.send(item.user?._id).subscribe({
        next: (res: any) => { item.friendship.requestId = res?.data?._id ?? null; this.cdr.markForCheck(); },
        error: (err: any) => {
          const code = err?.error?.statusCode ?? err?.status;
          if (code !== 409) item.friendship.status = 'NONE';
          const cooldownHours = this.friendRequestService.parseCooldownError(err);
          if (cooldownHours !== null) {
            this.friendLimitHours = cooldownHours;
            this.showFriendLimitPopup = true;
          }
          this.cdr.markForCheck();
        }
      });
    } else if (status === 'PENDING_SENT') {
      const prev = item.friendship.status;
      const prevId = item.friendship.requestId;
      item.friendship.status = 'NONE';
      this.cdr.markForCheck();
      this.friendRequestService.cancel(item.friendship.requestId).subscribe({
        error: () => { item.friendship.status = prev; item.friendship.requestId = prevId; this.cdr.markForCheck(); }
      });
    } else if (status === 'FRIENDS') {
      this.pendingUnfriendAction = () => {
        const prev = item.friendship.status;
        item.friendship.status = 'NONE';
        this.cdr.markForCheck();
        this.friendRequestService.unfriend(item.user?._id).subscribe({
          error: () => { item.friendship.status = prev; this.cdr.markForCheck(); }
        });
      };
      this.showUnfriendConfirm = true;
    }
  }

  onMentionClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.classList.contains('mention-highlight')) {
      event.stopPropagation();
      const username = target.getAttribute('data-username');
      if (username) {
        this.router.navigate(['/profile', username, 'post']);
      }
    }
  }

  private isLoggedIn(): boolean {
    return this.authState.getAuthSnapshot()?.isLoggedIn === true;
  }

  openLoginPrompt(userName: string, action: string): void {
    this.loginPromptUserName = userName;
    this.loginPromptAction = action;
    this.showLoginPrompt = true;
    this.cdr.markForCheck();
  }

  closeLoginPrompt(): void {
    this.showLoginPrompt = false;
    this.cdr.markForCheck();
  }

  goToLogin(): void {
    this.closeLoginPrompt();
    this.router.navigate(['/login']);
  }

  // ── Share ────────────────────────────────────────────────────────────────────

  openShareSheet(): void {
    this.shareCount = this.shareCount || this.post?.shares || 0;
    this.shareConfig = {
      contentId: this.postId ?? '',
      contentType: 'post',
      title: this.post?.text?.slice(0, 80) ?? 'Check out this post',
      description: this.post?.text?.slice(0, 160),
      imageUrl: this.post?.media?.[0]?.url,
    };
    this.cdr.markForCheck();
  }

  closeShareSheet(): void {
    this.shareConfig = null;
    this.cdr.markForCheck();
  }

  onShareCountUpdated(count: number): void {
    this.shareCount = count; // this.shareCount || this.post?.shares || 0;
    this.shareCount = count;
    this.cdr.markForCheck();
  }

  // ── Favourites ─────────────────────────────────────────────────────────────

  toggleFavourite(): void {
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(this.post?.user?.name ?? '', 'save to favourites');
      return;
    }
    if (!this.postId) return;
    const prev = this.isFavourite;
    this.isFavourite = !prev;
    this.cdr.markForCheck();
    this.favouriteService.toggle(this.postId).subscribe({
      next: (res: any) => {
        this.isFavourite = res?.data?.isFavourite ?? !prev;
        this.favouriteService.broadcastToggle(this.postId, this.isFavourite);
        this.cdr.markForCheck();
      },
      error: () => { this.isFavourite = prev; this.cdr.markForCheck(); },
    });
  }
}